import React, { ReactNode, useEffect } from 'react'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { fi, enUS, sv, de, nb } from 'date-fns/locale'
import { DEFAULT_LANGUAGE } from '../constants'
import i18next from 'i18next'
import { setDefaultOptions } from 'date-fns'
import { isValidLanguage } from '../utils/language'
import Language from '../enums/Language'

const LOCAL_STORAGE_KEY = 'language'
export interface LanguageState {
  language: Language
  setLanguage: (language: Language) => void
}

export const LanguageContext = React.createContext<LanguageState>({
  language: DEFAULT_LANGUAGE,
  setLanguage: (language: Language) => {
    window.localStorage.setItem(LOCAL_STORAGE_KEY, language)
  }
})

export const useLanguage = (): LanguageState => {
  return React.useContext(LanguageContext)
}

export interface LanguageProviderProps {
  children?: ReactNode
}

export const LanguageProvider = ({ children }: LanguageProviderProps) => {
  const getInitialLanguage = (): Language => {
    const language = window.localStorage.getItem(LOCAL_STORAGE_KEY) ?? DEFAULT_LANGUAGE

    if (isValidLanguage(language)) {
      return language
    }

    return DEFAULT_LANGUAGE
  }

  const [language, _setLanguage] = React.useState<Language>(getInitialLanguage())

  const getAdapterLocale = (language: Language): Locale => {
    switch (language) {
      case Language.FINNISH:
        return fi
      case Language.SWEDISH:
        return sv
      case Language.GERMAN:
        return de
      case Language.ENGLISH_US:
        return enUS
      case Language.NORWEGIAN_BOKMÅL:
        return nb
      default:
        return enUS // Fallback to English if no match
    }
  }

  const adapterLocale = getAdapterLocale(language)

  const setLanguage = (language: Language) => {
    if (isValidLanguage(language)) {
      localStorage.setItem(LOCAL_STORAGE_KEY, language)
      _setLanguage(language)
      i18next.changeLanguage(language)
    }
  }

  useEffect(() => {
    setDefaultOptions({ locale: adapterLocale })
  }, [adapterLocale])

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={adapterLocale}>
        {children}
      </LocalizationProvider>
    </LanguageContext.Provider>
  )
}
